<template>
  <div>
    <div id="transactions-comparable-table">
      <div class="m-2 flex justify-between items-center">
        <div v-if="aiEnabled" class="flex items-center gap-2">
          <span class="text-xs font-semibold bg-linear-to-r from-blue-500 to-cyan-500 px-1 text-white rounded-md">AI</span>
          <span class="mr-2">{{ $t(`messages.ai.model-type`) }}</span>
          <ModelSelector
            v-if="ebmModels?.length > 1"
            v-model="modelType"
            :models="pageProps?.modelsAvailable?.models"
            type="ebm"
            size="small"
            @change="refresh"
          />
          <span v-else-if="ebmModels?.length === 1" class="text-xs font-semibold bg-linear-to-r from-blue-500 to-cyan-500 px-2 py-1 text-white rounded-md">{{ ebmModels[0].label }}</span>
        </div>
        <div v-else></div>
        <div class="flex items-center gap-2">
          <span>{{ $t(`messages.markedtransactions.comparison-base`) }}</span>
          <SelectButton
            v-model="selectedPriceBase"
            :options="priceOptions"
            option-label="text"
            option-value="value"
            :allow-empty="false"
            size="small"
            @change="store.persistPriceMode()"
          />
        </div>
      </div>
      <div class="overflow-x-auto relative" ref="compGridContainer" @mouseleave="hoveredGroupHead = null">
        <!-- Grid wrapper: label column + N data columns (master + comparables) -->
        <div
          class="grid auto-rows-min border-b *:border-surface-200 [&>*:not(:first-child)]:border-l [&>*:not(:first-child)]:border-t min-w-[900px]"
          :style="{ gridTemplateColumns: gridTemplateColumns }"
          data-testid="comparison-table"
          :data-pending-cell-updates="pendingCellUpdates"
        >
          <!-- Header Row: top-left corner empty, then headers for each column except label -->
          <div class="sticky top-0 z-20 px-2 py-2 font-semibold border-t border-l">
          </div>
          <div
            v-for="field in gridFields.filter(f => f.key !== 'label')"
            :key="'hdr-' + field.id + '-' + field.key"
            class="sticky top-0 z-20 px-2 py-3 font-semibold"
            :class="getColumnAnimClass(field.key)"
            :data-col-key="field.key"
          >
            <div class="flex flex-col">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1">
                  <Button
                    v-if="field.key !== 'master' && hasDetailedAnalysis"
                    icon="pi pi-search-plus"
                    text
                    rounded
                    size="small"
                    aria-label="Analüüs"
                    @click="showAnalysisDialog(field.key)"
                    v-tooltip.bottom="`Kuva detailne analüüs`"
                  />
                  {{ field.label }}
                </div>
                <Button
                  v-if="field.key !== 'master' && hasPredictions"
                  icon="pi pi-refresh"
                  text
                  rounded
                  size="small"
                  aria-label="Leia uus sarnane tehing"
                  @click="findNewComparison(field.key)"
                  v-tooltip.bottom="`Leia uus sarnane tehing`"
                />
              </div>
              <div v-if="addDebug && field.key !== 'master' && hasPredictions" class="text-xs text-gray-500">
                {{ prediction.details_log?.[field.key]?.combined_distance }}
              </div>
            </div>
          </div>

          <!-- Body Rows -->
          <template v-for="(row, rowIndex) in comparisonTableRows" :key="row.key">
            <!-- Optional group header spanning all columns -->

            <!-- Label cell (sticky first column) -->
            <div
              :class="[
                'px-2 text-surface-800 font-semibold sticky left-0 z-10 flex items-center',
                row.key.includes('_raw') && hasPredictions ? 'ai bg-blue-100/75!' : '',
                isUserDefined(row.key) && isGroupStart(row.key) ? 'pl-0 py-0' : 'py-1',
                row.hidden ? 'row-hidden' : '',
                row.group
                  ? 'border-t-2! border-t-gray-600!'
                  : (rowIndex % 2 ? 'bg-surface-50' : 'bg-white')
              ]"
              @mouseenter="onRowHover(row.key)"
            >
              <template v-if="isCellEditable({ column_key: 'label', field_key: row.key })">
                <div class="cell">
                  <editable
                    show-icon
                    :class="`cell label editable ${
                      row.error && row.error === 'label' ? 'error' : ''
                    }`"
                    @focus="selectContent"
                    @blur="
                      (e) =>
                        handleCellUpdate(e.target.value.trim(), {
                          field: { key: 'label' },
                          item: row,
                        })
                    "
                    :model-value="row.label"
                  />
                </div>
                <Button
                  v-if="
                    isUserDefined(row.key) &&
                    isGroupStart(row.key)
                  "
                  size="small"
                  variant="link"
                  class="delete max-w-8"
                  @click="deleteCriteria(row.key)"
                  tabindex="-1"
                  :icon="`pi pi-trash`"
                />
              </template>
              <template v-else>
                <div v-if="row.key.includes('_raw') && hasPredictions" class="flex items-center text-blue-500 cursor-pointer hover:text-blue-600 gap-2" @click="showAdjustmentDialog(row.key)">
                  <span class="text-xs font-semibold bg-linear-to-r from-blue-500 to-cyan-500 px-1 text-white rounded-md">AI</span>
                  <span>Kohandus %</span>
                </div>
                <span v-else :class="{ 'line-through': row.hidden }" v-html="row.label"></span>
                <span v-if="row.hidden" class="hidden-pill">
                  {{ $t('messages.markedtransactions.hidden-pill') }}
                </span>
                <Select
                  v-if="row.key === 'm2hind'"
                  v-model="selectedUnitPriceBase"
                  :options="m2options"
                  optionLabel="text"
                  optionValue="value"
                  size="small"
                  class="m2select ml-2"
                  @change="store.persistPriceMode()"
                />
                <Button
                  v-if="store.isHideableComparisonGroup(row.key)"
                  size="small"
                  variant="link"
                  :class="['hide-row ml-auto max-w-8', { 'is-visible': row.hidden || hoveredGroupHead === row.key }]"
                  :icon="row.hidden ? 'pi pi-eye' : 'pi pi-eye-slash'"
                  v-tooltip.top="row.hidden
                    ? $t('messages.markedtransactions.restore-row')
                    : $t('messages.markedtransactions.hide-group-tooltip')"
                  tabindex="-1"
                  @click="toggleGroup(row)"
                />
              </template>
            </div>

            <!-- One cell per data column (master + comparables) -->
            <div
              v-for="field in gridFields.filter(f => f.key !== 'label')"
              :key="row.key + '-' + field.key"
              :data-testid="`comp-cell-${row.key}-${field.key}`"
              :class="[
                'px-2 py-1 leading-5 cell relative',
                row.group ? 'border-t-2! border-t-gray-600!' : '',
                row.hidden ? 'row-hidden' : '',
                row.key?.includes('_raw')
                  ? 'bg-blue-100/75'
                  : (rowIndex % 2 ? 'bg-surface-50' : 'bg-white'),
                getColumnAnimClass(field.key)
              ]"
              @mouseenter="onRowHover(row.key)"
            >
              <!-- RED-296: a hidden group's head row shows its values read-only
                   and dimmed — it is out of the report and the totals. -->
              <template v-if="row.hidden">
                <span class="block cell">{{ hiddenCellDisplay(row[field.key]) }}</span>
              </template>
              <template v-else-if="row.key === 'ad_link' && row[field.key]">
                <span class="cell flex items-start gap-1">
                  <a :href="row[field.key]" target="_blank" rel="noopener noreferrer" class="flex-1 min-w-0" data-testid="ad-link-cell-anchor">
                    {{ row[field.key] }}
                  </a>
                  <!-- RED-278: archived copy of the comparable's listing — only when a copy exists (prototype:
                       green-tinted square, no status text in the cramped cell) -->
                  <button
                    v-if="adArchiveEnabled && field.key !== 'master' && archivedComparableIds.has(field.key)"
                    type="button"
                    class="shrink-0 flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-green-200 bg-green-50 p-0 text-green-800 hover:bg-green-100"
                    v-tooltip.top="t('messages.transactions.ad_link.archive_open')"
                    :aria-label="t('messages.transactions.ad_link.archive_open')"
                    data-testid="ad-archive-open"
                    @click.stop="openAdArchive(field.key)"
                  >
                    <i class="pi pi-box text-xs"></i>
                  </button>
                </span>
              </template>
              <template v-else-if="row.key.includes('_raw') && hasPredictions && field.key !== 'master'">
                <div
                  class="ai-adjustment w-full"
                  :class="{ 'opacity-60 animate-pulse motion-reduce:animate-none transition-opacity duration-200': recomputing }"
                >
                  <div v-if="rawAdjustments(field.key)[row.key]" class="text-xs font-semibold rounded-md flex items-center gap-1">
                    <template
                      v-for="formatted in [formatAdjustmentValue(rawAdjustments(field.key)[row.key])]"
                      :key="formatted.main"
                    >
                      <div :class="getAdjustmentColorClass(rawAdjustments(field.key)[row.key])">
                        {{ formatted.sign }}<span class="text-base font-bold">{{ formatted.main }}</span>{{ formatted.rest }}
                      </div>
                    </template>
                  </div>
                  <div v-else class="text-xs font-light text-gray-500">
                    Kohandusi ei tehtud
                  </div>
                </div>
              </template>
              <template v-else-if="buildingRatingFields == row.key">
                <div class="cell editable" :class="{ 'imputed-cell': isImputedCell(row, field.key) }">
                  <span
                    v-if="isImputedCell(row, field.key)"
                    v-tooltip.top="$t('messages.markedtransactions.imputed-value-tooltip')"
                    class="imputed-pill"
                  >AI</span>
                  <!-- TODO: update model value to update building rating -->
                  <RatingMultiSelect
                    field="ehitise_seisukord"
                    v-model="row[field.key]"
                    :options="[1, 2, 3, 4, 5]"
                    placeholder="Vali kuni 2 hinnangut"
                    :maxSelectedLabels="2"
                    :selectionLimit="2"
                    size="small"
                    class="w-full max-w-96"
                    @update:model-value="handleCellUpdate($event, {
                      field: { key: field.key },
                      item: row,
                    })"
                    tooltip-position="left"
                  />
                </div>
              </template>
              <template v-else-if="buildingPartRatingFields == row.key">
                <div class="cell editable" :class="{ 'imputed-cell': isImputedCell(row, field.key) }">
                  <span
                    v-if="isImputedCell(row, field.key)"
                    v-tooltip.top="$t('messages.markedtransactions.imputed-value-tooltip')"
                    class="imputed-pill"
                  >AI</span>
                  <!-- TODO: update model value to update building rating -->
                  <RatingMultiSelect
                    field="hooneosa_seisukord"
                    v-model="row[field.key]"
                    :options="[1, 2, 3, 4, 5]"
                    placeholder="Vali kuni 2 hinnangut"
                    :maxSelectedLabels="2"
                    :selectionLimit="2"
                    size="small"
                    class="w-full max-w-96"
                    @update:model-value="handleCellUpdate($event, {
                      field: { key: field.key },
                      item: row,
                    })"
                    tooltip-position="left"
                  />
                </div>
              </template>
              <template v-else-if="parkingField === row.key">
                <div class="cell editable" :class="{ 'imputed-cell': isImputedCell(row, field.key) }">
                  <span
                    v-if="isImputedCell(row, field.key)"
                    v-tooltip.top="$t('messages.markedtransactions.imputed-value-tooltip')"
                    class="imputed-pill"
                  >AI</span>
                  <!-- TODO: update model value to update building rating -->
                  <RatingMultiSelect
                    field="kinnistu_parkimine"
                    v-model="row[field.key]"
                    :options="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
                    placeholder="Tee kuni 3 valikut"
                    :maxSelectedLabels="3"
                    :selectionLimit="3"
                    size="small"
                    class="w-full max-w-96"
                    @update:model-value="handleCellUpdate($event, {
                      field: { key: field.key },
                      item: row,
                    })"
                    tooltip-position="left"
                  />
                </div>
              </template>
              <template v-else-if="balconyField === row.key">
                <div class="cell editable" :class="{ 'imputed-cell': isImputedCell(row, field.key) }">
                  <div class="flex items-center gap-6 w-full">
                    <span
                      v-if="isImputedCell(row, field.key)"
                      v-tooltip.top="$t('messages.markedtransactions.imputed-value-tooltip')"
                      class="imputed-pill"
                    >AI</span>
                    <span v-if="hasBalcony(field.key)" class="w-16">
                      {{ hasBalcony(field.key) }} m²
                    </span>
                    <RatingMultiSelect
                      field="rodu"
                      v-model="row[field.key]"
                      :options="[0, 1, 2, 3]"
                      placeholder="Tee kuni 3 valikut"
                      :maxSelectedLabels="3"
                      :selectionLimit="3"
                      size="small"
                      class="w-full max-w-96"
                      tooltip-position="left"
                      @update:model-value="handleCellUpdate($event, {
                        field: { key: field.key },
                        item: row,
                      })"
                    />
                  </div>
                </div>
              </template>
              <template v-else-if="storageField === row.key">
                <div class="cell editable" :class="{ 'imputed-cell': isImputedCell(row, field.key) }">
                  <span
                    v-if="isImputedCell(row, field.key)"
                    v-tooltip.top="$t('messages.markedtransactions.imputed-value-tooltip')"
                    class="imputed-pill"
                  >AI</span>
                  <RatingMultiSelect
                    field="panipaik"
                    v-model="row[field.key]"
                    :options="[0, 1, 2]"
                    placeholder="Tee kuni 2 valikut"
                    :maxSelectedLabels="2"
                    :selectionLimit="2"
                    size="small"
                    class="w-full max-w-96"
                    tooltip-position="left"
                    @update:model-value="handleCellUpdate($event, {
                      field: { key: field.key },
                      item: row,
                    })"
                  />
                </div>
              </template>
              <template v-else-if="isDualAreaCell(row, field.key)">
                <!-- RED-235 Q6: both suletud netopind and eluruumi pind stay
                     user-correctable in every column the dual area appears
                     (master + comparables), matching the Ostu-müügitehingute
                     tabel. The dual cell only renders for üksikelamu columns, so
                     non-üksikelamu areas keep their single read-only value via
                     the branch below. -->
                <div
                  :class="[
                    'cell editable flex items-center gap-1',
                    isCellInvalid(snpFieldKey, field.key) || isCellInvalid(selectedUnitPriceBase, field.key) ? 'error' : '',
                  ]"
                >
                  <editable
                    class="p-1 block flex-1 min-w-0"
                    @focus="selectContent"
                    @blur="(e) => handleDualAreaUpdate(e.target.value.trim(), field.key, snpFieldKey, row[field.key + dualSnpSuffix])"
                    :model-value="invalidCellText(snpFieldKey, field.key) ?? row[field.key + dualSnpSuffix]"
                  />
                  <span class="shrink-0 text-gray-400">/</span>
                  <editable
                    class="p-1 block flex-1 min-w-0"
                    @focus="selectContent"
                    @blur="(e) => handleDualAreaUpdate(e.target.value.trim(), field.key, selectedUnitPriceBase, row[field.key])"
                    :model-value="invalidCellText(selectedUnitPriceBase, field.key) ?? row[field.key]"
                  />
                </div>
              </template>
              <template v-else>
                <template v-if="isCellEditable({column_key: field.key, field_key: row.key})">
                  <editable
                    show-icon
                    :multiline="row.key === 'aadress' || row.key === 'tehnovorgud'"
                    :class="`p-1 block cell editable ${
                      (row.error && row.error === field.key) || isCellInvalid(row.key, field.key) ? 'error' : ''
                    }`"
                    @focus="selectContent"
                    @blur="
                      (e) => handleCellUpdate(e.target.value.trim(), { field: { key: field.key }, item: row })
                    "
                    :model-value="invalidCellText(row.key, field.key) ?? row[field.key]"
                  />
                  <span
                    v-if="store.isFieldOverridden(field.key, row.key)"
                    class="edited-pill edited-pill-overlay"
                    v-tooltip.top="editedTooltip(field.key, row.key)"
                    @click="confirmRevert($event, field.key, row.key)"
                  >
                    <i class="pi pi-undo"></i>
                  </span>
                </template>
                <span
                  v-else
                  class="block cell"
                >
                  {{ formatCellValue(row[field.key]) }}
                </span>
              </template>
            </div>
          </template>
        </div>
        <!-- Column overlay spanning only the replacing column across the grid -->
        <div
          v-if="replacingColumnKey && animationPhase === 'replacing'"
          class="pointer-events-none absolute top-0 bottom-0 z-20"
          :style="overlayColumnStyle"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="h-full w-full flex items-start pt-20 justify-center bg-white/60 dark:bg-black/40 backdrop-blur-xs">
            <div class="flex flex-col items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <div class="flex items-end gap-1 h-4">
                <span class="w-2 h-2 rounded-full bg-blue-500 animate-bounce motion-reduce:animate-none"></span>
                <span class="w-2 h-2 rounded-full bg-purple-500 animate-bounce motion-reduce:animate-none [animation-delay:0.12s]"></span>
                <span class="w-2 h-2 rounded-full bg-cyan-500 animate-bounce motion-reduce:animate-none [animation-delay:0.24s]"></span>
              </div>
              <div>{{ $t('messages.markedtransactions.replacing') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="m-2 flex justify-end items-center gap-2">
        <Button size="sm" variant="success" @click="store.addComparisonCriteria">
          {{ $t(`messages.markedtransactions.add`) }}
        </Button>

        <Button variant="outline-primary" size="sm" @click="exportExcel">
          <span class="pi pi-file-excel"></span>
          {{ $t('messages.markedtransactions.export') }}
        </Button>
      </div>
    </div>

    <Dialog v-model:visible="adjustmentDialogVisible" :header="adjustmentsMapping?.label" :style="{ width: '60rem' }">
      <div class="text-surface-700 dark:text-surface-400 block mb-2">Tunnused</div>
      <div class="text-sm text-gray-500 mb-4">
        Kohanduste aluseks olevad tunnused
      </div>
      <div class="flex gap-8 justify-between mb-4 text-sm">
        <div class="flex flex-wrap gap-x-4 gap-y-2 w-full">
          <div v-for="item in adjustmentsMapping?.features" :key="item.original_column">
            <span>{{ item.label || item.original_column }}</span>
          </div>
        </div>
      </div>
    </Dialog>


    <!-- Per-comparable contribution tree (RED-262): same explorer patterns as
         the admin model explorer — |mõju| sorting, kind badges, score rows. -->
    <ComparableAnalysisDialog v-model:visible="analysisDialogVisible" :comparable-id="selectedComparableId" />
    <AdArchiveDialog v-if="adArchiveEnabled" v-model:visible="adArchiveVisible" :transaction-id="adArchiveTransactionId" />

    <!-- RED-297: anchored restore-to-original confirmation (grouped so the
         global ConfirmDialog in AppLayout doesn't also fire) -->
    <ConfirmPopup group="revert-original-comparison" />
  </div>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
} from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { useFeature } from '@/composables/useFeature'
import { useModelResolution } from '@/composables/useModelResolution'
import { useTransactionComparisonMasterDetails } from '@/composables/useTransactionComparisonMasterDetails'
import {
  collapseDualAreaForExport,
  DUAL_AREA_SNP_SUFFIX,
} from '@/services/areaDisplay'
import { adjustmentColorClass as getAdjustmentColorClass } from '@/services/contributionTree'
import { isNumeric, selectContent } from '@/services/utilities'
import { useTransactionStore } from '@/stores/transaction'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import Editable from '../editable.vue'
import ModelSelector from '../form/ModelSelector.vue'
import RatingMultiSelect from '../RatingMultiSelect.vue'
import AdArchiveDialog from './AdArchiveDialog.vue'
import ComparableAnalysisDialog from './ComparableAnalysisDialog.vue'

const { t } = useI18n()
const page = usePage()
const toast = useToast()
const confirm = useConfirm()
const compGridContainer = shallowRef(null)
const store = useTransactionStore()
const evaluationStore = useEvaluationStore()
// RED-262: true while AI adjustments recompute after a pivot edit. The table stays
// mounted; only the adjustment values gently dim/pulse (see .ai-adjustment binding).
const recomputing = computed(() => evaluationStore.recomputingAdjustments)
const { ensureModelsResolved } = useModelResolution()
const {
  markedTableConf,
  selectedUnitPriceBase,
  selectedPriceBase,
  comparisonTableRows,
  comparisonTableFields,
} = storeToRefs(store)

const { storeMasterData } = useTransactionComparisonMasterDetails()

// RED-278: archived listing dialog, opened per comparable from the ad_link row.
const adArchiveEnabled = useFeature('ad-archive')
const adArchiveVisible = shallowRef(false)
const adArchiveTransactionId = shallowRef(null)
const openAdArchive = transactionId => {
  adArchiveTransactionId.value = Number(transactionId)
  adArchiveVisible.value = true
}
// Comparables are loaded through the details endpoint, whose cache carries the
// archive summary (composeData() keeps only table fields on the row itself).
const archivedComparableIds = computed(() => {
  const details = store.transactionDetails
  return new Set(
    comparisonTableFields.value
      .filter(field => field.key !== 'master' && field.key !== 'label' && details[field.id]?.ad_archive?.archived_at)
      .map(field => field.key),
  )
})

// RED-267: land valuation is manual-only — hide the AI model selector row
// on a maatükk kohandustabel.
const aiEnabled = computed(() => !evaluationStore.isLandParcel)

const buildingRatingFields = 'ehitise_seisukord'
const buildingPartRatingFields = 'hooneosa_seisukord'
const parkingField = 'kinnistu_parkimine'
const balconyField = 'rodu'
const storageField = 'panipaik'

// RED-296: hide/unhide criterion groups (Lea Kull: unneeded rows had to be
// deleted from the Word report by hand). A hidden group stays in the table as
// its dimmed head row; only exports and totals exclude it.

// The eye toggle reveals on hover over ANY row of its group. Cells of one
// group are sibling grid nodes with no shared wrapper, so CSS :hover can't
// scope this — a tiny hover tracker does.
const hoveredGroupHead = ref(null)

const rowGroupHeads = computed(() => {
  const groups = markedTableConf.value?.groups || []
  const map = {}
  let currentHead = null
  comparisonTableRows.value.forEach(row => {
    if (groups.includes(row.key)) {
      currentHead = row.key
    }
    map[row.key] = currentHead
  })
  return map
})

const onRowHover = key => {
  hoveredGroupHead.value = rowGroupHeads.value[key] ?? null
}

// Rating heads carry array values; everything else renders as-is.
const hiddenCellDisplay = value => {
  if (Array.isArray(value)) return value.join(', ')
  return value ?? ''
}

const toggleGroup = row => {
  if (row.hidden) {
    store.unhideComparisonGroup(row.key)
  } else {
    hideGroup(row.key)
  }
}

// RED-297: edited-cell marker + safe restore-to-original (changes pivot).
const formatOriginalValue = value =>
  value === null || value === undefined || value === '' ? '–' : value

const editedTooltip = (columnKey, fieldKey) =>
  t('messages.markedtransactions.edited-tooltip', {
    value: formatOriginalValue(store.getOriginalFieldValue(columnKey, fieldKey)),
  })

const confirmRevert = (event, columnKey, fieldKey) => {
  confirm.require({
    group: 'revert-original-comparison',
    target: event.currentTarget,
    message: t('messages.markedtransactions.revert-confirm', {
      value: formatOriginalValue(store.getOriginalFieldValue(columnKey, fieldKey)),
    }),
    icon: 'pi pi-undo',
    acceptLabel: t('messages.markedtransactions.revert-accept'),
    rejectLabel: t('messages.markedtransactions.revert-reject'),
    accept: () => store.revertTransactionField(columnKey, fieldKey),
  })
}

const hideGroup = head => {
  // Warn when the group already carries adjustments: they stop counting
  // toward sum_kohandus while hidden.
  const kohandusKeys = store
    .comparisonGroupMembers(head)
    .filter(key => key.endsWith('_kohandus'))
  const dataColumnKeys = comparisonTableFields.value
    .filter(f => !['label', 'btn'].includes(f.key))
    .map(f => f.key)
  const hasAdjustments = comparisonTableRows.value.some(
    row =>
      kohandusKeys.includes(row.key) &&
      dataColumnKeys.some(col => {
        const value = row[col]
        return value !== null && value !== undefined && value !== '' && Number(value) !== 0
      }),
  )

  store.hideComparisonGroup(head)

  if (hasAdjustments) {
    toast.add({
      severity: 'warn',
      summary: t('messages.markedtransactions.hidden-row-toast-summary'),
      detail: t('messages.markedtransactions.hidden-row-toast-detail'),
      life: 5000,
    })
  }
}


// RED-235: the "Vara pindala" row shows an üksikelamu column as the dual
// "suletud netopind / eluruumi pind" area. Both halves stay separately editable
// in every üksikelamu column — master and comparables alike (Toomas Q6: the user
// may need to correct either value). dualCols on the row marks which columns are
// üksikelamu.
const dualSnpSuffix = DUAL_AREA_SNP_SUFFIX
const snpFieldKey = 'ehitise_suletud_netopind'

const isDualAreaCell = (row, columnKey) =>
  Array.isArray(row.dualCols) && row.dualCols.includes(columnKey)

// RED-267: a land parcel prices per m² of the plot — single option.
const m2options = computed(() =>
  evaluationStore.isLandParcel
    ? [
        {
          value: 'maatuki_pindala',
          text: t(`messages.markedtransactions.fields.maatuki_pindala`),
        },
      ]
    : [
        {
          value: 'korteri_pind',
          text: t(`messages.markedtransactions.fields.korteri_pind`),
        },
        {
          value: 'hooneosa_pind',
          text: t(`messages.markedtransactions.fields.hooneosa_pind`),
        },
      ],
)
const priceOptions = [
  {
    value: 'tehingusumma',
    text: t(`messages.markedtransactions.opts.tehingusumma`),
  },
  { value: 'm2hind', text: t(`messages.markedtransactions.opts.m2hind`) },
]

const hasBalcony = computed(() => {
  return id => {
    if (id === 'master') {
      if (!evaluationStore.selectedBuildingPart) {
        return false
      }

      if (
        !evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind'] ||
        evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind'] ===
          '-' ||
        evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind'] == '0'
      ) {
        return false
      }

      return evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind']
    }

    const row = Object.values(store.comparisonTableData).find(
      t => t.id === Number(id),
    )

    if (!row) {
      return false
    }

    if (
      !row['hooneosa_valipindade_pind'] ||
      row['hooneosa_valipindade_pind'] === '-' ||
      row['hooneosa_valipindade_pind'] == '0'
    ) {
      return false
    }

    return row['hooneosa_valipindade_pind']
  }
})

const pageProps = computed(() => page.props)

const addDebug = computed(() => {
  return pageProps.value?.addDebug === true
})

const modelType = computed({
  get() {
    return evaluationStore.modelType
  },
  set(value) {
    evaluationStore.modelType = value
    evaluationStore.modelTypePickedByUser = true
  },
})

const prediction = computed(() => evaluationStore.prediction)

const ebmModels = computed(() =>
  pageProps.value?.modelsAvailable?.models
    ?.filter(m => m.type === 'ebm')
    ?.slice()
    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    ?.map(model => ({
      label: model.name,
      value: model.model,
      metrics: model.metrics,
      created_at: model.created_at,
    })),
)

const analysisDialogVisible = shallowRef(false)
const selectedComparableId = shallowRef(null)
const adjustmentDialogVisible = shallowRef(false)
const adjustmentCategory = shallowRef('')

// Column replace animation/overlay state
const replacingColumnKey = shallowRef(null)
const animationPhase = shallowRef('idle')
let replaceAnimTimeout = null

const getColumnAnimClass = key => {
  if (!key || key === 'label') return ''
  if (replacingColumnKey.value !== key) return ''
  if (animationPhase.value === 'out') return 'col-replace-out'
  if (animationPhase.value === 'in') return 'col-replace-in'
  return ''
}

// Compute overlay pixel position to precisely cover the replacing column
const overlayColumnStyle = computed(() => {
  const container = compGridContainer.value
  if (!container) return {}
  if (!replacingColumnKey.value) return {}

  // Find the header cell for the replacing column
  const headerEl = container.querySelector(
    `[data-col-key="${CSS.escape(replacingColumnKey.value)}"]`,
  )
  if (!headerEl) return {}

  const containerRect = container.getBoundingClientRect()
  const headerRect = headerEl.getBoundingClientRect()

  const left = Math.max(
    0,
    headerRect.left - containerRect.left + container.scrollLeft,
  )
  const width = Math.min(container.scrollWidth, headerRect.width)

  return {
    left: left + 'px',
    width: width + 'px',
  }
})

// Fields used in grid (exclude the trailing action column "btn")
const gridFields = computed(() => {
  return (comparisonTableFields.value || []).filter(f => f.key !== 'btn')
})

// CSS grid template: one fixed label column + N data columns
const gridTemplateColumns = computed(() => {
  const dataColCount = Math.max((gridFields.value.length || 1) - 1, 0)
  return `minmax(220px,320px) repeat(${dataColCount}, minmax(280px,1fr))`
})

const formatCellValue = value => {
  if (value === null || value === undefined || value === '') return '\u00A0'
  return value
}

// RED-262: ML-imputed condition value in a comparable column (never master \u2014
// subjects are never imputed). Cleared by the store when the user edits.
const isImputedCell = (row, columnKey) =>
  columnKey !== 'master' && columnKey !== 'label' && !!row.imputedBy?.[columnKey]

const isCellEditable = data => {
  const editables = markedTableConf.value?.editables || []
  const masterEditables = markedTableConf.value?.master_editables || []
  const userDefined = markedTableConf.value?.user || {}

  return (
    (data.column_key !== 'master' &&
      data.column_key !== 'label' &&
      editables.includes(data.field_key)) ||
    (data.column_key === 'label' &&
      Object.keys(userDefined).includes(data.field_key) &&
      editables.includes(data.field_key) &&
      isGroupStart(data.field_key)) ||
    (data.column_key === 'master' && masterEditables.includes(data.field_key))
  )
}

// RED-313: one debounce PER CELL. A single shared debounce dropped the earlier
// of two blurs landing within 300 ms (Tab out of a cell into the next one,
// then click away) — the first edit was never saved.
const cellUpdateRunners = new Map()
// RED-368 test seam: cell updates that are scheduled or still running,
// rendered as `data-pending-cell-updates` on the grid. The E2E specs wait for
// it to reach 0 instead of sleeping past the debounce. A cell counts once
// while its debounce waits and once per run in flight, so a blur that lands
// during a running update keeps the count above 0 until its own run ends.
const pendingCellUpdates = shallowRef(0)
const scheduledCells = new Set()
const perCellDebounced = (key, fn) => {
  if (!cellUpdateRunners.has(key)) {
    const run = async (...args) => {
      scheduledCells.delete(key)
      try {
        await fn(...args)
      } finally {
        pendingCellUpdates.value -= 1
      }
    }
    cellUpdateRunners.set(key, debounce(run, 300))
  }
  const runner = cellUpdateRunners.get(key)
  return (...args) => {
    if (!scheduledCells.has(key)) {
      scheduledCells.add(key)
      pendingCellUpdates.value += 1
    }
    return runner(...args)
  }
}

// RED-313: cells holding non-numeric text where a number is required. The
// text is kept as the cell's model (a re-render would otherwise rewrite the
// input from the untouched row value and wipe what was typed) and the cell is
// tinted (`error` class) until a number is entered — no toast, no console
// noise, nothing of the user's is discarded. Keyed `${fieldKey}:${columnKey}`.
const invalidCells = ref({})
const invalidCellKey = (fieldKey, columnKey) => `${fieldKey}:${columnKey}`
const invalidCellText = (fieldKey, columnKey) =>
  invalidCells.value[invalidCellKey(fieldKey, columnKey)]
const isCellInvalid = (fieldKey, columnKey) =>
  invalidCellText(fieldKey, columnKey) !== undefined
const setCellInvalid = (fieldKey, columnKey, text) => {
  const key = invalidCellKey(fieldKey, columnKey)
  if (text !== undefined) {
    invalidCells.value = { ...invalidCells.value, [key]: text }
  } else if (key in invalidCells.value) {
    const { [key]: _removed, ...rest } = invalidCells.value
    invalidCells.value = rest
  }
}

const isEmptyCellValue = v => v === null || v === undefined || v === ''

/**
 * Numeric gate for a cell blur.
 * - 'save'  → a number; may be saved.
 * - 'clear' → '' on a cell that held a value: the valuer emptied it.
 * - 'skip'  → '' on an already-empty cell (Tab passing through) — not input;
 *             or non-numeric text, which stays in the input with the cell
 *             flagged until a number is entered. Never a toast, never a
 *             console error, never discards what was typed.
 */
const numericGate = (value, fieldKey, columnKey, currentValue) => {
  if (value === '') {
    setCellInvalid(fieldKey, columnKey, undefined)
    return isEmptyCellValue(currentValue) ? 'skip' : 'clear'
  }
  if (!isNumeric(value)) {
    setCellInvalid(fieldKey, columnKey, value)
    return 'skip'
  }
  setCellInvalid(fieldKey, columnKey, undefined)
  return 'save'
}

// Emptying a numeric cell. Subject column: the value goes away for the
// session (same home as every other subject edit). Comparable column: an
// overridden cell gets its original back (what the orange marker does), an
// untouched one is emptied for the session and marked so the register value
// can be restored.
const clearNumericCell = async (transactionId, fieldKey) => {
  if (transactionId !== 'master' && store.isFieldOverridden(transactionId, fieldKey)) {
    await store.revertTransactionField(transactionId, fieldKey)
    return
  }
  await store.updateComparisonTransactionField({ transactionId, fieldKey, value: null })
}

const runCellUpdate = async (value, data) => {
  const { field, item } = data
  const transactionId = field.key
  const rowField = item.key

  if (transactionId === 'label') {
    store.updateUserDefinedFieldLabel(rowField, value)
    return
  }

  if (markedTableConf.value?.numeric?.includes(rowField)) {
    const verdict = numericGate(value, rowField, transactionId, item[transactionId])
    if (verdict === 'skip') return
    if (verdict === 'clear') {
      await clearNumericCell(transactionId, rowField)
      return
    }
  }

  await store.updateComparisonTransactionField({
    transactionId: transactionId,
    fieldKey: rowField,
    value: value,
  })
}

const handleCellUpdate = (value, data) =>
  perCellDebounced(`${data.field.key}:${data.item.key}`, runCellUpdate)(value, data)

// RED-235: persist one half of the master column's dual area. The SNP input
// writes ehitise_suletud_netopind; the eluruumi input writes the active price
// base (hooneosa_pind / korteri_pind), so processedMasterData's derived
// vara_pind reflects the correction. Both are areas → numeric gate as above.
const runDualAreaUpdate = async (value, transactionId, fieldKey, currentValue) => {
  const verdict = numericGate(value, fieldKey, transactionId, currentValue)
  if (verdict === 'skip') return
  if (verdict === 'clear') {
    await clearNumericCell(transactionId, fieldKey)
    return
  }

  await store.updateComparisonTransactionField({ transactionId, fieldKey, value })
}

const handleDualAreaUpdate = (value, transactionId, fieldKey, currentValue) =>
  perCellDebounced(`${transactionId}:${fieldKey}`, runDualAreaUpdate)(
    value,
    transactionId,
    fieldKey,
    currentValue,
  )

// Helpers for AI adjustment rows and analysis coloring
const hasPredictions = computed(() => {
  return prediction.value && Object.keys(prediction.value).length > 0
})

// Check if prediction has detailed analysis data (feature_groups, original_values, top_contributions)
// QuickValuation predictions don't include this data, so analysis dialog would be empty
const hasDetailedAnalysis = computed(() => {
  return (
    prediction.value?.feature_groups &&
    Object.keys(prediction.value.feature_groups).length > 0
  )
})

const rawAdjustments = computed(() => {
  return id => prediction.value?.grouped_differences_unrounded?.[id] || {}
})

const adjustmentsMapping = computed(() => {
  if (!evaluationStore.prediction.feature_groups) {
    return []
  }
  return evaluationStore.prediction.feature_groups[adjustmentCategory.value]
})

const showAdjustmentDialog = key => {
  adjustmentDialogVisible.value = true
  adjustmentCategory.value = key.replace('_raw', '')
}

const showAnalysisDialog = comparableId => {
  selectedComparableId.value = comparableId
  analysisDialogVisible.value = true
}

const isUserDefined = key =>
  Object.keys(markedTableConf.value?.user || {}).includes(key)

const deleteCriteria = key => {
  store.deleteComparisonCriteria(key)
}

const exportExcel = async () => {
  try {
    const response = await axios.post(
      '/export-excel',
      {
        // RED-235: collapse the dual SNP/eluruumi cells into a single
        // "snp / eluruumi" string so the Excel export shows one value per cell.
        rows: collapseDualAreaForExport(
          comparisonTableRows.value.filter(
            row => !row.key.includes('_raw') && !row.hidden,
          ),
        ),
        cols: comparisonTableFields.value.filter(f => f.key !== 'btn'),
        selectedPriceBase: selectedPriceBase.value,
        selectedUnitPriceBase: selectedUnitPriceBase.value,
      },
      {
        responseType: 'blob',
      },
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `comparison_table_${new Date().toISOString().split('T')[0]}.xlsx`,
    )
    document.body.appendChild(link)
    link.click()
    link.remove()
  } catch (error) {
    console.error('Export failed:', error)
  }
}

const formatAdjustmentValue = value => {
  if (value === undefined || value === null || value === '') {
    return { sign: '', main: '', rest: '' }
  }
  const stringValue = String(value)
  const match = stringValue.match(/^(-|\+)?(\d+)/)

  if (!match) {
    return { sign: '', main: stringValue, rest: '' }
  }

  const sign = match[1] || ''
  const numberPart = match[2]
  const restOfString = stringValue.substring(match[0].length)

  const main = numberPart.substring(0, 2)
  const rest = numberPart.substring(2) + restOfString

  return { sign, main, rest }
}

const isGroupStart = key => (markedTableConf.value?.groups || []).includes(key)

// Refresh prediction when model type changes
const refresh = async () => {
  ensureModelsResolved()

  const newPrediction = await evaluationStore.predict()
  if (newPrediction) {
    await evaluationStore.updatePrediction(newPrediction)

    evaluationStore.save({
      comparison_table_state: {
        comparisonComponentPage: 1,
      },
    })

    store.setComparisonPage(1)
  }
}

const findNewComparison = async fieldKey => {
  const replaceId = Number.parseInt(fieldKey)
  if (!replaceId || fieldKey === 'master') return

  // Combine current marked transactions with previously replaced IDs to avoid cycling
  const currentMarked = [...store.markedTransactions]
  const previouslyReplaced = [...(evaluationStore.replacedComparableIds || [])]
  const seenIds = [...new Set([...currentMarked, ...previouslyReplaced])]
  const keepIds = currentMarked.filter(id => id !== replaceId)

  // Remember current index and remove the old row in-place
  const replaceIndex = store.selectionTableRows.findIndex(
    row => row.id === replaceId,
  )

  // Show overlay immediately without removing the column yet
  replacingColumnKey.value = fieldKey
  animationPhase.value = 'replacing'

  const payload = {
    model_type: modelType.value,
    evaluation_id: evaluationStore.id,
    building_part_id:
      evaluationStore.selectedBuildingPart['ehitise-osa-number'],
    replace_id: replaceId,
    seen_ids: seenIds,  // All seen IDs for exclusion in candidate search
    current_ids: currentMarked,  // Current comparables for building final set
    recent_months: evaluationStore.recencyFilter,
    // Same similarity overrides as predict() — the replacement must come
    // from the same pool as the comparables it joins.
    ...evaluationStore.buildSimilarityOverridesPayload(),
  }

  try {
    const response = await axios.post(
      '/api/building-part/evaluation/replace',
      payload,
    )
    const data = response.data
    if (!data) return

    // Determine the new id returned by API
    const returnedIds = (data.similar_properties || []).map(p => p.property_id)
    let newId = returnedIds.find(id => !keepIds.includes(id))
    if (!newId && returnedIds.length) {
      newId = returnedIds[0]
    }

    // Track the replaced ID to avoid cycling back to it
    evaluationStore.addReplacedComparableId(replaceId)

    if (newId && replaceIndex !== -1) {
      // Remove the old (replaced) row from selectionTableRows
      store.selectionTableRows.splice(replaceIndex, 1)

      // Keep markedTransactions ordering aligned to retain column position
      const ordered = [...keepIds]
      ordered.splice(replaceIndex, 0, newId)
      store.markedTransactions = ordered
    }

    // Update prediction data but preserve the markedTransactions we just set
    await evaluationStore.updatePrediction(data, { preserveMarkedTransactions: true })

    // Move the new row to the correct position (where the old one was)
    if (newId && replaceIndex !== -1) {
      const newRowCurrentIdx = store.selectionTableRows.findIndex(row => row.id === newId)
      if (newRowCurrentIdx !== -1) {
        const [newRow] = store.selectionTableRows.splice(newRowCurrentIdx, 1)
        store.selectionTableRows.splice(replaceIndex, 0, newRow)
      }
    }

    // Animate the incoming column (use newId if available) and hide overlay
    const inKey = newId ? String(newId) : fieldKey
    replacingColumnKey.value = inKey
    animationPhase.value = 'in'
    if (replaceAnimTimeout) clearTimeout(replaceAnimTimeout)
    replaceAnimTimeout = setTimeout(() => {
      animationPhase.value = 'idle'
      replacingColumnKey.value = null
    }, 700)
  } catch (err) {
    console.error('Failed to replace comparable', err)
    // Reset animation on error
    animationPhase.value = 'idle'
    replacingColumnKey.value = null
  }
}

onBeforeMount(() => {
  storeMasterData()
})

onBeforeUnmount(() => {
  if (replaceAnimTimeout) clearTimeout(replaceAnimTimeout)
  replaceAnimTimeout = null
})

// RED-296: the selectedBuildingPart reconfigure watcher moved to
// DetailComparableTransactionsComponent — it must outlive this component's
// page-1-only mount. initComparisonTableConfig below self-heals on mount.

// Keep overlay aligned on horizontal scroll
// Using passive listener to avoid scroll jank
onMounted(() => {
  store.initComparisonTableConfig({
    hasBuildingPart: evaluationStore.selectedBuildingPart !== null,
  })

  const el = compGridContainer.value
  if (!el) return
  const onScroll = () => {
    // Trigger computed style re-evaluation
    // By updating a dummy ref; but here, access overlayColumnStyle to re-compute
    void overlayColumnStyle.value
  }
  el.addEventListener('scroll', onScroll, { passive: true })
  onBeforeUnmount(() => {
    el.removeEventListener('scroll', onScroll)
  })
})
</script>

<style lang="css">
  @reference '../../../css/tailwind.css';

  .delete {
    color: #ff0000;
    font-size: 1.2rem;
    padding: 0;
    margin: 0;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .delete:hover {
    background: #ff0000;
    color: #fff;
  }

  /* RED-296: eye toggle on group-head label cells. Revealed via the JS
     group-hover tracker (.is-visible) so hovering ANY cell of the group shows
     it; always visible on a hidden (dimmed) row as the restore affordance. */
  .hide-row {
    color: #6c757d;
    padding: 0;
    height: 100%;
    display: flex;
    align-items: center;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .hide-row:hover {
    color: #1d4ed8;
  }

  .hide-row.is-visible,
  .hide-row:focus-visible {
    opacity: 1;
  }

  /* RED-296: a hidden group's collapsed head row — stays in the table but is
     visibly out of the report and the totals. */
  .row-hidden {
    background: var(--p-surface-100, #f3f4f6) !important;
    color: var(--p-surface-400, #9ca3af) !important;
  }

  .row-hidden .cell {
    color: inherit;
  }

  .hidden-pill {
    flex-shrink: 0;
    margin-left: 0.5rem;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0 0.35rem;
    border-radius: 9999px;
    border: 1px solid var(--p-surface-300, #d1d5db);
    color: var(--p-surface-500, #6b7280);
    background: var(--p-surface-0, #fff);
  }

  #transactions-comparable-table {


    .ai, .ai-adjustment {

      font-size: 0.8rem;
      height: 100%;
      display: flex;
      align-items: center;
      padding: 0.2rem 0.4rem;
    }

    .cell {
      padding: 0;
      margin: 0;
      @apply inline-flex w-full items-center;

      .cell {
        padding: 0.4rem 0.4rem;
        height: 100%;
        line-height: 1.4rem;

        &.editable {
          background: #f1fff7;
        }

        &.error {
          background: #ffe9e9;
        }

        /* RED-262: ML-imputed condition value — blue tint + inline AI pill,
           matching the AI adjustment-row styling (bg-blue-100/75 + gradient). */
        &.imputed-cell {
          background: rgb(219 234 254 / 0.75);
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
      }
      min-height: 32px; /* compact row height */

    }
  }

  /* RED-297: hand-edited value marker — amber sibling of the AI imputed-pill.
     Clickable: opens the anchored restore-to-original confirmation. */
  .edited-pill {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 600;
    cursor: pointer;
    padding: 1px 4px;
    margin-right: 4px;
    border-radius: 4px;
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fcd34d;
  }

  .edited-pill:hover {
    background: #fde68a;
  }

  /* Overlay variant: pinned to the cell's right edge so the pill's appearance
     never shifts the input it annotates. */
  .edited-pill-overlay {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    margin-right: 0;
    z-index: 5;
  }

  .imputed-pill {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    color: #fff;
    padding: 3px 4px;
    border-radius: 4px;
    background: linear-gradient(to right, #3b82f6, #06b6d4);
    cursor: help;
  }

  /* Column replace animations */
  @keyframes colFadeSlideOut {
    0% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(6px); }
  }
  @keyframes colFadeSlideIn {
    0% { opacity: 0; transform: translateY(-6px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  .col-replace-out {
    animation: colFadeSlideOut 300ms ease-in forwards;
  }
  .col-replace-in {
    animation: colFadeSlideIn 450ms ease-out forwards;
  }
</style>
